import styled from '@emotion/styled';
import { theme } from '@arnold/common';

export const TableFrame = styled.div`
  min-width: 0;
  max-width: 100%;
  border: 1px solid ${theme.colors.borderSeparator.default};
  border-radius: 12px;
  overflow: hidden;
  background: ${theme.colors.backgroundCover.default};
  color: ${theme.colors.text.primary};
`;

export const TableScroller = styled.div<{ scrollEnabled: boolean }>`
  min-width: 0;
  max-width: 100%;
  overflow-x: ${({ scrollEnabled }) => (scrollEnabled ? 'auto' : 'hidden')};
  overscroll-behavior-x: contain;
`;

export const StyledTable = styled.table`
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  line-height: 1.45;
  th,
  td {
    box-sizing: border-box;
    padding: 14px min(16px, calc(var(--cell-width, 120px) / 8));
    border-bottom: 1px solid ${theme.colors.borderSeparator.default};
    text-align: left;
    vertical-align: top;
    overflow: hidden;
    overflow-wrap: anywhere;
  }
  th {
    position: relative;
    background: ${theme.colors.backgroundBasic.default};
    color: ${theme.colors.text.secondary};
    font-size: 12px;
    font-weight: 500;
    padding-top: 12px;
    padding-bottom: 12px;
    vertical-align: middle;
    user-select: none;
  }
  th[data-dragging='true'] {
    background: ${theme.colors.backgroundBasic.active};
  }
  th[data-drop-side='before'] {
    box-shadow: inset 3px 0 ${theme.colors.brand.primary};
  }
  th[data-drop-side='after'] {
    box-shadow: inset -3px 0 ${theme.colors.brand.primary};
  }
  tbody tr:last-child td {
    border-bottom: 0;
  }
  tbody tr:hover td {
    background: ${theme.colors.backgroundBasic.default};
  }
  tr[data-comparison-role] td {
    border-left: 3px solid transparent;
  }
  tr[data-comparison-role='filtered'] td {
    background: ${theme.colors.emotionInformative.background};
    font-weight: 500;
  }
  tr[data-comparison-role='filtered'] td:first-child {
    border-left-color: ${theme.colors.emotionInformative.default};
  }
  tr[data-comparison-role='filtered']:hover td {
    background: ${theme.colors.emotionInformative.background};
  }
  tr[data-comparison-divider] td {
    padding: 2px 16px;
    border-bottom: 0;
  }
`;

export const ComparisonBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${theme.colors.emotionInformative.background};
  color: ${theme.colors.emotionInformative.default};
  border-bottom: 1px solid ${theme.colors.borderSeparator.default};
  font-size: 13px;
  svg {
    flex-shrink: 0;
  }
`;

export const ComparisonBadge = styled.span<{ role: 'baseline' | 'filtered' }>`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${({ role }) =>
    role === 'filtered' ? theme.colors.emotionInformative.background : theme.colors.backgroundBasic.active};
  color: ${({ role }) => (role === 'filtered' ? theme.colors.emotionInformative.default : theme.colors.text.secondary)};
`;

export const ComparisonDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  span {
    flex-shrink: 0;
    font-size: 11px;
    color: ${theme.colors.text.secondary};
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px solid ${theme.colors.borderSeparator.default};
  }
  &:before,
  &:after {
    content: '';
    flex: 1;
    border-top: 1px dashed ${theme.colors.borderSeparator.default};
  }
`;

export const ColumnHeading = styled.div`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  /* Make header padding clickable too; drag/resize handles sit above this target. */
  &:after {
    content: '';
    position: absolute;
    inset: 0;
  }
  &:hover {
    color: ${theme.colors.brand.primary};
  }
`;

export const SearchToolbar = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${theme.colors.borderSeparator.default};
`;

export const SearchField = styled.label`
  display: flex;
  flex: 1 1 200px;
  flex-direction: column;
  min-width: 0;
  gap: 4px;
  color: ${theme.colors.text.secondary};
  font-size: 12px;
  input,
  select {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    border: 1px solid ${theme.colors.borderSeparator.default};
    border-radius: 6px;
    background: ${theme.colors.backgroundCover.default};
    color: ${theme.colors.text.primary};
    padding: 8px;
    font: inherit;
  }
`;

export const SearchHint = styled.div`
  flex-basis: 100%;
  font-size: 12px;
  color: ${theme.colors.text.secondary};
`;

export const ResizeHandle = styled.div<{ resizing: boolean }>`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  bottom: 0;
  width: 9px;
  cursor: col-resize;
  touch-action: none;
  user-select: none;
  &:after {
    content: '';
    position: absolute;
    right: 3px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: ${({ resizing }) => (resizing ? theme.colors.brand.primary : theme.colors.borderSeparator.default)};
  }
  &:hover:after {
    background: ${theme.colors.brand.primary};
  }
`;

export const RowIdentity = styled.div<{ depth: number }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  padding-left: ${({ depth }) => Math.min(depth * 18, 108)}px;
  > :last-child {
    min-width: 0;
    flex: 1;
  }
`;

export const SmallButton = styled.button`
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${theme.colors.brand.primary};
  cursor: pointer;
  padding: 3px 4px;
  font: inherit;
  font-size: 12px;
  text-align: left;
  &:hover {
    background: ${theme.colors.backgroundBasic.active};
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const ColumnDragHandle = styled(SmallButton)`
  position: relative;
  z-index: 1;
  flex: 0 0 18px;
  padding: 0;
  text-align: center;
  cursor: grab;
  touch-action: none;
  user-select: none;
  &:active {
    cursor: grabbing;
  }
`;

export const Disclosure = styled(SmallButton)`
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 50%;
  background: ${theme.colors.backgroundBasic.default};
  color: ${theme.colors.text.secondary};
`;

export const DisclosureSpace = styled.span`
  flex: 0 0 24px;
  width: 24px;
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid ${theme.colors.borderSeparator.default};
  font-size: 12px;
  color: ${theme.colors.text.secondary};
`;

export const PageButton = styled(SmallButton)<{ active?: boolean }>`
  min-width: 30px;
  height: 30px;
  text-align: center;
  background: ${({ active }) => (active ? theme.colors.brand.primary : 'transparent')};
  color: ${({ active }) => (active ? theme.colors.textInverted.primary : theme.colors.text.primary)};
  &:hover {
    background: ${({ active }) => (active ? theme.colors.brand.primary : theme.colors.backgroundBasic.active)};
  }
`;

export const Muted = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: 12px;
`;

export const EmptyState = styled.div`
  padding: 28px 16px;
  color: ${theme.colors.text.secondary};
  text-align: center;
`;

export const CellStack = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 8px;
`;

export const MetricLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding-top: 4px;
  > :first-child {
    min-width: 0;
    flex: 1;
  }
  strong {
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  min-width: 0;
`;

export const Chip = styled.span`
  display: inline-block;
  max-width: 100%;
  padding: 3px 6px;
  border-radius: 4px;
  background: ${theme.colors.backgroundBasic.active};
  font-size: 11px;
  overflow-wrap: anywhere;
`;
